import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiFetch } from "@/lib/api";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { hasPerm } from "@/lib/auth";

function nf(v: any) {
  if (v === null || v === undefined) return "0";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString();
}

type LoanDueCard = {
  label: string;
  value: string | number;
  gradient: string;
  href: string;
};

type SummaryItem = {
  label: string;
  value: string | number;
  badgeClass: string;
  href: string;
};

type Balances = {
  till?: { value?: number | string; updated_at?: string | null };
  paybill?: { value?: number | string; updated_at?: string | null };
  bulk?: { value?: number | string; updated_at?: string | null };
  sms?: { value?: number | string; updated_at?: string | null };
};

export default function HomePage() {
  const dir =
    (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";

 

  const [loading, setLoading] = useState(true);
  const [home, setHome] = useState<any>(null);
  const [err, setErr] = useState("");
   const canMpesaBal =  hasPerm("mpesa-bal"); //home.flags.can_mpesa_bal

  const [balances, setBalances] = useState<Balances | null>(null);
  const [balancesLoading, setBalancesLoading] = useState(false);

  // 1) Load HOME summary (always)
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const json = await apiFetch<any>(`/home`);
        if (!alive) return;

        setHome(json.data);
       // setFlags(json.flags || {}); // 👈 IMPORTANT
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message || "Failed to load dashboard");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // 2) Load balances ONLY if permitted
  useEffect(() => {
    let alive = true;

    if (!canMpesaBal) {
      setBalances(null);
      return;
    }

    (async () => {
      try {
        setBalancesLoading(true);
        const json = await apiFetch<any>(`/home/balances`);
        if (!alive) return;

        setBalances(json.data);
      } catch {
        // if 403 or any error: hide balances silently
        if (!alive) return;
        setBalances(null);
      } finally {
        if (alive) setBalancesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [canMpesaBal]);

  const loanDueCards: LoanDueCard[] = useMemo(() => {
    return [
      {
        label: "LOANS DUE TODAY",
        value: nf(home?.loanDueToday),
        gradient: "gradient-card-green",
        href: "/actions/due/today",
      },
      {
        label: "LOANS DUE - NEXT 3 DAYS",
        value: nf(home?.loanDueNext3Days),
        gradient: "gradient-card-green",
        href: "/actions/due/due-next-3days",
      },
      {
        label: "INSTALLMENT DUE TODAY",
        value: nf(home?.installmentDueToday),
        gradient: "gradient-card-blue",
        href: "/actions/due/installment-due-today",
      },
      {
        label: "DEFAULTED INSTALLMENTS",
        value: nf(home?.defaultedInstallments),
        gradient: "gradient-card-blue",
        href: "/actions/due/defaulted-installment",
      },
    ];
  }, [home]);

  const summaryItems: SummaryItem[] = useMemo(() => {
    return [
      {
        label: "CO Pending Loans",
        value: nf(home?.coPendingLoans) ?? 0,
        badgeClass: "bg-chart-green/20 text-chart-green",
        href: "/actions/loans/co",
      },
      {
        label: "Loans Pending Approval",
        value: nf(home?.loansPendingApproval) ?? 0,
        badgeClass: "bg-chart-blue/20 text-chart-blue",
        href: "/actions/loans/pending",
      },
      {
        label: "Loans Pending Disbursement",
        value: nf(home?.loansPendingDisbursement) ?? 0,
        badgeClass: "bg-chart-green/20 text-chart-green",
        href: "/actions/loans/pending-disbursement",
      },
      {
        label: "Active Loans",
        value: nf(home?.activeLoans) ?? 0,
        badgeClass: "bg-chart-blue/20 text-chart-blue",
        href: "/actions/loans/active",
      },
      {
        label: "Customer Pending Approval",
        value: nf(home?.customerPendingApproval) ?? 0,
        badgeClass: "bg-chart-orange/20 text-chart-orange",
        href: "/actions/customers/pending",
      },
      {
        label: "Incomplete Profile",
        value: nf(home?.incompleteProfile) ?? 0,
        badgeClass: "bg-chart-purple/20 text-chart-purple",
        href: "/actions/customers/incomplete",
      },
    ];
  }, [home]);

  const balanceCards = useMemo(() => {
    const b = balances;
    return [
      {
        title: "Till Balance",
        value: nf(b?.till?.value),
        tm: b?.till?.updated_at,
        gradient: "gradient-card-red",
      },
      {
        title: "Paybill Balance",
        value: nf(b?.paybill?.value),
        tm: b?.paybill?.updated_at,
        gradient: "gradient-card-blue",
      },
      {
        title: "Bulk Balance",
        value: nf(b?.bulk?.value),
        tm: b?.bulk?.updated_at,
        gradient: "gradient-card-green",
      },
      {
        title: "SMS Balance",
        value: nf(b?.sms?.value),
        tm: b?.sms?.updated_at,
        gradient: "gradient-card-red",
      },
    ];
  }, [balances]);

  const pinTop = isRTL ? "left-2" : "right-2";
  const pinTop3 = isRTL ? "left-3" : "right-3";

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-foreground"
        >
          {greeting}, Admin
        </motion.h1>
      </div>

      {/* Error */}
      {err ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">{err}</CardContent>
        </Card>
      ) : null}

      {/* Loan Due Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {loanDueCards.map((card, i) => (
          <Link
            key={card.label}
            to={card.href}
            className={loading ? "pointer-events-none" : "group"}
            aria-disabled={loading}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`${
                loading ? "bg-muted" : card.gradient
              } relative rounded-xl p-4 text-center cursor-pointer hover:opacity-95 active:opacity-90`}
            >
              <span
                className={`absolute ${pinTop} top-2 opacity-0 group-hover:opacity-100 transition`}
              >
                <ExternalLink className="h-4 w-4 text-primary-foreground/90" />
              </span>

              <p className="text-2xl font-bold text-primary-foreground">
                {loading ? "—" : card.value}
              </p>
              <p className="text-xs font-medium text-primary-foreground/80 mt-1">
                {card.label}
              </p>
            </motion.div>
          </Link>
        ))}
      </div>

      {/* Summary */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {summaryItems.map((item, i) => (
              <Link
                key={item.label}
                to={item.href}
                className={loading ? "pointer-events-none" : "group"}
                aria-disabled={loading}
              >
                <Card className="shadow-card p-4 relative cursor-pointer hover:bg-muted/30 transition">
                  <span
                    className={`absolute ${pinTop3} top-3 opacity-0 group-hover:opacity-100 transition`}
                  >
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </span>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex flex-col items-center text-center gap-2"
                  >
                    <span
                      className={[
                        "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-sm font-bold",
                        item.badgeClass,
                      ].join(" ")}
                    >
                      {loading ? "—" : item.value}
                    </span>

                    <span className="text-xs text-muted-foreground leading-tight">
                      {item.label}
                    </span>
                  </motion.div>
                </Card>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Balance Cards (ONLY if has mpesa-bal) */}
      {canMpesaBal && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {balanceCards.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="overflow-hidden shadow-card">
                <div className={`${b.gradient} px-4 py-3`}>
                  <p className="text-sm font-semibold text-primary-foreground">
                    {b.title}
                  </p>
                </div>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">
                    {balancesLoading ? "—" : b.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Last Updated: {balancesLoading ? "—" : b.tm || "—"}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}